import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Utils from '../../felles/Utils';
import MenuEl from '../../models/MenuEl';
import MenuItemTarget from '../../models/MenuItemTarget';
import * as actions from '../../store/actions/index';
import { AppState } from '../../store/rootReducer';
import { StyledTreeItem, useStyles } from './styles/LPNavMenuTreeStyles';

/**
 * Navigation menu tree component using MUI X SimpleTreeView.
 * Displays hierarchical menu items and handles navigation.
 */
export default function LPNavMenuTree() {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state selectors
  const menuItems = useSelector((state: AppState) => state.menuItem.menuItems);
  const expanded = useSelector((state: AppState) => state.menuItem.expanded);
  const selected = useSelector((state: AppState) => state.menuItem.selected);
  const isLoggedIn = useSelector((state: AppState) => state.auth.authenticated);

  // Fetch menu items on mount if not already loaded
  useEffect(() => {
    if (menuItems.length === 0 && isLoggedIn) {
      dispatch(actions.menuItemGet());
    }
  }, [dispatch, menuItems.length, isLoggedIn]);

  /**
   * Creates a MenuItemTarget from a menu element
   */
  const createTarget = useCallback((item: MenuEl, isRoot = false): MenuItemTarget => {
    if (isRoot && item.hasChildren && item.nodes[0]) {
      const node = item.nodes[0];
      return new MenuItemTarget(
        item.rootName, node.name, '', '',
        node.tilpasset, node.prodSynlig, node.kodeSynlig,
        node.labelSynlig, node.menuSynlig, item.hasChildren, item.description
      );
    }
    return new MenuItemTarget(
      item.rootName, isRoot ? item.rootName : item.name, '', '',
      item.tilpasset, item.prodSynlig, item.kodeSynlig,
      item.labelSynlig, item.menuSynlig, item.hasChildren, item.description
    );
  }, []);

  /**
   * Handles root menu item click
   */
  const handleRootClick = useCallback((event: React.MouseEvent, item: MenuEl) => {
    event.stopPropagation();
    event.preventDefault();

    const isExpanded = expanded.includes(item.rootName);
    dispatch(actions.menuItemSetExpanded(isExpanded ? [] : [item.rootName]));
    dispatch(actions.menuItemSetTarget(createTarget(item, true)));
    dispatch(actions.menuItemSetSelected([
      item.rootName,
      item.nodes[0]?.name + item.rootName || item.rootName
    ]));
    dispatch(actions.resetPredefinedReduxTransactionStore());
    navigate(`${Utils.HOMEPAGE_CONTEXT}/menu`);
  }, [dispatch, expanded, navigate, createTarget]);

  /**
   * Handles sub-item click
   */
  const handleSubItemClick = useCallback((event: React.MouseEvent, subItem: MenuEl) => {
    event.stopPropagation();
    event.preventDefault();

    dispatch(actions.menuItemSetTarget(createTarget(subItem)));
    dispatch(actions.menuItemSetSelected([subItem.rootName, subItem.name + subItem.rootName]));
    dispatch(actions.resetPredefinedReduxTransactionStore());
    navigate(`${Utils.HOMEPAGE_CONTEXT}/menu`);
  }, [dispatch, navigate, createTarget]);

  /**
   * Handles tree expansion state change
   */
  const handleExpandedItemsChange = useCallback((_: React.SyntheticEvent, itemIds: string[]) => {
    dispatch(actions.menuItemSetExpanded(itemIds));
  }, [dispatch]);

  /**
   * Handles tree selection state change
   */
  const handleSelectedItemsChange = useCallback((_: React.SyntheticEvent, itemIds: string | string[] | null) => {
    if (itemIds) {
      dispatch(actions.menuItemSetSelected(Array.isArray(itemIds) ? itemIds : [itemIds]));
    }
  }, [dispatch]);

  /**
   * Renders a tree item label
   */
  const renderLabel = useCallback((text: string) => (
    <div className={classes.labelRoot}>
      <Typography className={classes.labelText}>
        {text}
      </Typography>
    </div>
  ), [classes]);

  /**
   * Memoized tree items - only recalculates when menuItems change
   */
  const treeItems = useMemo(() => {
    if (menuItems.length === 0) return null;

    return menuItems.map((item) => (
      <StyledTreeItem
        key={item.rootName}
        itemId={item.rootName}
        onClick={(e) => handleRootClick(e, item)}
        label={renderLabel(item.name)}
        classes={{ content: classes.root }}
      >
        {item.heading !== '' && item.nodes
          .filter((subItem) => subItem.heading !== '')
          .map((subItem) => (
            <StyledTreeItem
              key={`${subItem.name}-${subItem.rootName}`}
              itemId={`${subItem.name}${subItem.rootName}`}
              onClick={(e) => handleSubItemClick(e, subItem)}
              label={renderLabel(subItem.name)}
              classes={{ content: classes.subroot }}
            />
          ))
        }
      </StyledTreeItem>
    ));
  }, [menuItems, handleRootClick, handleSubItemClick, renderLabel, classes.root]);

  return (
    <SimpleTreeView
      expandedItems={expanded}
      selectedItems={selected}
      onExpandedItemsChange={handleExpandedItemsChange}
      onSelectedItemsChange={handleSelectedItemsChange}
      slots={{
        collapseIcon: ExpandMoreIcon,
        expandIcon: ChevronRightIcon,
      }}
    >
      {treeItems ?? <div>Loading menu ...</div>}
    </SimpleTreeView>
  );
}